import { useState } from 'react';
import { Input, Button } from '..';

interface DynamicUrlListProps {
  label: string;
  placeholder?: string;
  urls: string[];
  onChange: (urls: string[]) => void;
  maxUrls?: number;
}

export default function DynamicUrlList({
  label,
  placeholder = 'https://...',
  urls,
  onChange,
  maxUrls = 10,
}: DynamicUrlListProps) {
  const [tempUrl, setTempUrl] = useState('');
  const [error, setError] = useState('');

  const validateUrl = (url: string): boolean => {
    try {
      new URL(url);
      return url.startsWith('http://') || url.startsWith('https://');
    } catch {
      return false;
    }
  };

  const handleAdd = () => {
    setError('');

    if (!tempUrl.trim()) {
      setError('Please enter a URL');
      return;
    }

    if (!validateUrl(tempUrl)) {
      setError('Please enter a valid URL (must start with http:// or https://)');
      return;
    }

    if (urls.includes(tempUrl)) {
      setError('This URL is already added');
      return;
    }

    if (urls.length >= maxUrls) {
      setError(`Maximum of ${maxUrls} URLs allowed`);
      return;
    }

    onChange([...urls, tempUrl]);
    setTempUrl('');
  };

  const handleRemove = (index: number) => {
    onChange(urls.filter((_, i) => i !== index));
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAdd();
    }
  };

  return (
    <div className="space-y-4">
      <label className="block text-sm font-semibold dark:text-gray-100">
        {label}
      </label>

      {/* URL List */}
      {urls.length > 0 && (
        <div className="space-y-2">
          {urls.map((url, index) => (
            <div
              key={index}
              className="flex items-center gap-2 p-3 border-2 border-gray-200 dark:border-gray-700 rounded-lg bg-gray-50 dark:bg-gray-800"
            >
              <span className="flex-1 text-sm truncate dark:text-gray-100">
                {url}
              </span>
              <button
                type="button"
                onClick={() => handleRemove(index)}
                className="text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-300 p-1"
              >
                <svg
                  className="w-5 h-5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Add URL Input */}
      <div className="flex gap-2">
        <div className="flex-1">
          <Input
            type="url"
            placeholder={placeholder}
            value={tempUrl}
            onChange={(e) => setTempUrl(e.target.value)}
            onKeyPress={handleKeyPress}
            error={error}
          />
        </div>
        <Button
          type="button"
          variant="secondary"
          onClick={handleAdd}
          disabled={urls.length >= maxUrls}
        >
          + Add
        </Button>
      </div>

      {/* Counter */}
      <p className="text-xs text-gray-500 dark:text-gray-400">
        {urls.length} / {maxUrls} URLs added
      </p>
    </div>
  );
}
